import React from 'react';
import classes from './Badge.module.scss';
import classNames from 'classnames';
import { IconsEnum } from '../../../constants/icons';
import { BadgeColorsEnum, BadgeSizesEnum } from '../../../constants/badge';
import { CategoryColor } from '../../../models/graphql';
import { Icon } from '../../images/Icon/Icon';
import { Tooltip } from '../../actions/Tooltip';
import { Void } from '../../../models/basic-objects';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { capitalize } from 'lodash';

export type BadgeBaseProps = {
  color: CategoryColor | BadgeColorsEnum;
  size: BadgeSizesEnum;
  text: string;
  clickHandler?: Void;
  shrinkContent?: boolean;
  children?: never;
};

export type BadgeWithTooltipProps = {
  tooltipText: string;
  badgeKey: string;
};

export type BadgeWithoutTooltipProps = {
  badgeKey?: never;
  tooltipText?: never;
};

export type BadgeWithIconProps = {
  icon: IconsEnum;
  side: 'left' | 'right';
};

export type BadgeWithoutIconProps = {
  icon?: never;
  side?: never;
};

export type BadgeViewProps = (BadgeWithoutTooltipProps | BadgeWithTooltipProps) &
  (BadgeWithIconProps | BadgeWithoutIconProps) &
  BadgeBaseProps;

export const BadgeView = (props: BadgeViewProps): JSX.Element => {
  const badgeClasses = classNames(
    classes.Badge,
    classes[`BackgroundColor${props.color}`],
    classes[`BadgeColor${props.color}`],
    classes[`BadgeSize${props.size}`],
    { [classes.BadgePointer]: props.clickHandler, [classes.BadgeShrinkContent]: props.shrinkContent }
  );

  const badgeLabelClasses = classNames(
    props.tooltipText ? classes.BadgeLabelWithTooltip : classes.BadgeLabel,
    props.icon ? (props.tooltipText ? classes.BadgeLabelWithTooltipWithIcon : classes.BadgeLabelWithIcon) : null,
    'cell',
    'shrink',
    props.side === 'right' ? 'small-order-1' : 'small-order-2'
  );

  const ButtonContentElem = (
    <>
      {props.icon && (
        <div
          className={classNames(
            classes.BadgeIcon,
            classes[`BadgeIcon${capitalize(props.side)}`],
            'cell shrink',
            props.side === 'right' ? 'small-order-2' : 'small-order-1'
          )}>
          <Icon name={props.icon} size={'small'} />
        </div>
      )}
      <div
        className={classNames(
          'cell shrink',
          badgeLabelClasses,
          props.side === 'right' ? 'small-order-1' : 'small-order-2'
        )}>
        {props.size === BadgeSizesEnum.LARGE ? <h6>{props.text}</h6> : <div>{props.text}</div>}
      </div>
    </>
  );

  const badgeLabel = props.tooltipText ? (
    <>
      <div id={`badge-${props.badgeKey}`} className={`grid-x align-left align-middle`}>
        {ButtonContentElem}
      </div>
      <Tooltip
        type={'regular'}
        target={`#badge-${props.badgeKey}`}
        pointer={true}
        autoZIndex={false}
        style={{ zIndex: scssVars['z-index-overmodal'] }}>
        <>{props.tooltipText}</>
      </Tooltip>
    </>
  ) : (
    <div className={`grid-x align-left align-middle`}>{ButtonContentElem}</div>
  );

  return (
    <>
      <div
        className={badgeClasses}
        onClick={props.clickHandler}
        id={`badge-${props.tooltipText ? props?.badgeKey : ''}`}>
        {badgeLabel}
      </div>
    </>
  );
};
